// aiWidgets.js — the client half of AI-CREATED home widgets (tickets/opus.md
// "Per-workspace AI-CREATED widgets"), the second half of Nika's mandate
// "new apps should be able to have their own widgets from appstore or created
// by user ai". The APP-STORE half is the module-manifest `widgets: []` contract
// (pages/main.js HomeModuleWidget / _adoptModuleWidgets); this plane reuses ALL
// of its hosting mechanics and adds only what a module-less widget needs.
//
// ─── The one hard constraint ─────────────────────────────────────────────────
// An AI-created widget's body is DATA, never CODE. Nothing here — and nothing
// in WidgetRenderer — ever evaluates a stored string: no eval, no Function, no
// `extends` of an author-supplied component key. A body is a fixed vocabulary
// of block types whose fields are PATHS into that widget's own resolved
// dataRecipe result, and the six interpreters live in reviewed code.
//
// The server is the security boundary (server/src/domains/widgets/
// widgetDefSchema.js validates every write, and re-checks the VIEWING user's
// capability on every read). Everything here is the mirror of that contract:
// it re-checks defensively and degrades, but it never grants.
//
// Pure + sync unless noted, and registered as project functions so the
// component reaches them via `el.call('fn', …)` — never a cross-file import,
// and never a module-scope const read from a factory (frank drops those).

// Bare shared-library import, deferred-call style — the shell's function
// modules all reach the shared barrel this way (circular-init safety).
import { shellSay } from './shellSharedDelegates.js'

// A bundle sentence for the widget's capability notice: through the calling
// element's polyglot (`el.call('aiWidgetCapability', …)` binds it, so the notice
// follows the session language), or the SHIPPED ENGLISH on a bare call — never a
// raw key.

// ─── Plane resolution ────────────────────────────────────────────────────────

/**
 * The AI-created widget defs that belong to the workspace the tab is SHOWING.
 *
 * Rows are stamped server-side with their own `workspaceId`, and this filter
 * pins them to the live workspace record. That makes a cross-workspace leak
 * structurally impossible even mid-switch: a row fetched for the previous
 * workspace cannot render under the next one, no matter how the async writes
 * interleave (the same failure class the `_wsPanelsCacheKey` envelope exists
 * to prevent — a bare list had no way to say which workspace it described).
 */
export const aiWidgetsFor = (root) => {
  const rows = Array.isArray(root && root.workspaceAiWidgets) ? root.workspaceAiWidgets : []
  if (!rows.length) return []
  const live = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
  return rows.filter((w) => {
    if (!w || !w.id) return false
    if (!live || !w.workspaceId) return true
    return String(w.workspaceId) === String(live)
  })
}

/** Just the ids — the gate/fold bookkeeping in pages/main.js reads this. */
export const aiWidgetIdsFor = (root) => {
  const ids = new Set()
  for (const w of aiWidgetsFor(root)) ids.add(w.id)
  return ids
}

/**
 * The header subtitle for an AI widget: who made it. A module widget names its
 * owning APP when two widgets would otherwise read as one (_moduleWidgetSubtitle);
 * an AI widget has no app, so it names its creator — and says so, because "this
 * was generated for you" is the single most useful thing its header can carry.
 */
export const aiWidgetOwnerLabel = (widget) => {
  if (!widget || !widget.aiGenerated) return ''
  const who = widget.createdByName
  return who ? `AI · ${who}` : 'AI'
}

// ─── Data binding ────────────────────────────────────────────────────────────

// Segments that could reach the prototype chain. The server's path regex
// already rejects these on write; re-checked here so a row written before a
// future schema change (or hand-inserted into the collection) still cannot
// walk out of its own data.
const FORBIDDEN_SEGMENTS = ['__proto__', 'prototype', 'constructor']

/**
 * Resolve a validated data path (`total`, `rows[0].name`, `summary.today.gross`)
 * against the widget's OWN resolved dataRecipe result.
 *
 * Own-property walk only, and undefined on ANY miss — a path that does not
 * resolve renders an em-dash, never a crash and never a neighbouring value.
 */
// A TEXT block's typographic style (design-d2: the key is `textStyle`; `tone`
// is its old name, still read): 'caption' or 'body'. Only a text block has one.
export const aiWidgetTextStyle = (block) => {
  const b = block && typeof block === 'object' ? block : {}
  if (b.type !== 'text-block') return 'body'
  const style = b.textStyle != null ? b.textStyle : b.tone
  return style === 'caption' ? 'caption' : 'body'
}

// A SEMANTIC tone (a board block's `data.tone`: neutral | good | warning |
// critical) as the design system's semantic ink; neutral and anything else
// carry none.
export const aiWidgetToneInk = (tone) =>
  ({ good: 'successInk', warning: 'warningInk', critical: 'dangerInk' })[tone] || null

export const resolveWidgetPath = (data, path) => {
  if (data == null || typeof path !== 'string' || !path) return undefined
  const segments = path.split(/[.[\]]+/).filter(Boolean)
  let cursor = data
  for (const segment of segments) {
    if (cursor == null || typeof cursor !== 'object') return undefined
    if (FORBIDDEN_SEGMENTS.indexOf(segment) !== -1) return undefined
    if (!Object.prototype.hasOwnProperty.call(cursor, segment)) return undefined
    cursor = cursor[segment]
  }
  return cursor
}

/** A bound field read off ONE item of a list block — same own-property rule. */
export const readWidgetField = (item, field) => {
  if (!item || typeof item !== 'object' || typeof field !== 'string' || !field) return undefined
  if (FORBIDDEN_SEGMENTS.indexOf(field) !== -1) return undefined
  if (!Object.prototype.hasOwnProperty.call(item, field)) return undefined
  return item[field]
}

/**
 * A bound list, normalized: always an array, always bounded, and every entry
 * pre-flattened to `{ key, label, value, meta }` so the DOMQL children factory
 * that draws it stays a plain map over state (no per-row logic in the view).
 *
 * `limit` is capped at 20 by the server's own schema; re-clamped here because a
 * runaway list is a layout failure, not a data one.
 */
export const widgetListItems = (data, block) => {
  if (!block) return []
  const raw = resolveWidgetPath(data, block.itemsPath)
  const rows = Array.isArray(raw) ? raw : []
  const limit = Math.min(Math.max(Number(block.limit) || 5, 1), 20)
  const labelField = block.labelField || block.titleField || 'name'
  const valueField = block.valueField || 'value'
  const metaField = block.metaField || null
  return rows.slice(0, limit).map((row, i) => {
    const label = readWidgetField(row, labelField)
    const value = readWidgetField(row, valueField)
    const meta = metaField ? readWidgetField(row, metaField) : undefined
    return {
      key: `${i}`,
      label: label == null ? '—' : String(label),
      value: typeof value === 'number' ? value : Number(value) || 0,
      rawValue: value,
      meta: meta == null ? '' : String(meta)
    }
  })
}

/** The largest bound value in a list — the denominator for bar widths. */
export const widgetListPeak = (items) => {
  let peak = 0
  for (const item of Array.isArray(items) ? items : []) {
    if (item && item.value > peak) peak = item.value
  }
  return peak
}

/**
 * Format ONE bound value for display. The `format` enum is fixed by the server
 * schema (number | currency | percent | text) — an unknown value falls through
 * to text rather than throwing, because a render must never be the thing that
 * fails.
 */
export const formatWidgetValue = (value, format, opts) => {
  const options = opts || {}
  if (value == null || value === '') return '—'
  const prefix = options.prefix || ''
  const suffix = options.suffix || ''
  if (format === 'text' || typeof value === 'boolean') return `${prefix}${value}${suffix}`
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) return `${prefix}${value}${suffix}`
  if (format === 'percent') {
    return `${prefix}${Math.round(num * (Math.abs(num) <= 1 ? 100 : 1))}%${suffix}`
  }
  if (format === 'currency') {
    return `${prefix}${num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}${suffix}`
  }
  return `${prefix}${num.toLocaleString()}${suffix}`
}

// ─── Capability (render-side, defensive) ─────────────────────────────────────

/**
 * The VIEWING user's capability for a widget, as the server resolved it for
 * THIS caller (`viewerCapability` is stamped on every listed row) folded with
 * whatever the live fetch actually did.
 *
 * Fails CLOSED on an unreadable/absent annotation: a row that arrived without
 * one is treated as not-granted, because "we could not tell" and "you may see
 * it" must never be the same answer on a capability boundary.
 *
 * @returns {{ allowed: boolean, reason: string, notice: string }}
 */
export function aiWidgetCapability(widget, fetchState) {
  const stamped = widget && widget.viewerCapability
  const stampedAllowed = !!(stamped && stamped.allowed === true)
  const denied = fetchState && fetchState.denied
  const allowed = stampedAllowed && !denied
  const reason = denied ? 'denied_at_read' : (stamped && stamped.reason) || 'unknown'
  return {
    allowed,
    reason,
    notice: allowed
      ? ''
      : reason === 'unknown_source'
        ? shellSay(this, 'workspace.aiWidget.notice.sourceGone')
        : shellSay(this, 'workspace.aiWidget.notice.noAccess')
  }
}

// ─── Fetch (the ONLY data path — SDK, per-viewer, never the creator's) ───────

/**
 * Resolve ONE widget's dataRecipe for the CURRENTLY VIEWING user and stash the
 * result on `root._aiWidgetData[<id>]`.
 *
 * Three properties this function exists to guarantee:
 *   1. It goes through `sdk.execute(...)` — the same declarative-fetch path
 *      every other surface uses, so the server authorizes the VIEWER on every
 *      call. The creator's authorization is never cached, borrowed or replayed.
 *   2. A 403/permission failure lands as `{ denied: true }`, which the renderer
 *      draws as an inert notice — degraded, never broken and never blank-with-
 *      a-console-error.
 *   3. It is idempotent per (widget, workspace): a repeat call while a request
 *      is in flight, or after one settled, is a no-op — the hosts re-render on
 *      every root update and would otherwise refetch forever.
 *
 * Results live on `root`, not on the host's own state, because the widget hosts
 * are keyed children of a factory over `_allModuleWidgetsFor` — their state is
 * REASSIGNED from that array on every recompute, so anything parked there is
 * silently dropped on the next root update.
 */
export const loadAiWidgetData = async (root, sdk, widget) => {
  if (!root || !sdk || !widget || !widget.id) return
  const recipe = widget.dataRecipe
  if (!recipe || !recipe.from) return
  const stamped = widget.viewerCapability
  // Never spend a request on a widget the server already told this viewer they
  // cannot read.
  if (!stamped || stamped.allowed !== true) return

  const wsId = (root.workspace && (root.workspace.id || root.workspace._id)) || null
  const bag = root._aiWidgetData || {}
  const current = bag[widget.id]
  const sig = `${wsId || ''}/${widget.updatedAt || ''}`
  if (current && current.sig === sig && (current.loading || current.settled)) return

  const write = (patch) => {
    const next = { ...(root._aiWidgetData || {}) }
    next[widget.id] = { ...(next[widget.id] || {}), sig, ...patch }
    root.update({ _aiWidgetData: next }, { preventFetch: true })
  }

  write({ loading: true, settled: false, denied: false, error: null })
  try {
    const params = { ...(recipe.params || {}), ...(wsId ? { workspaceId: wsId } : {}) }
    const data = await sdk.execute(recipe.from, recipe.method || 'list', params)
    write({ loading: false, settled: true, data: normalizeAiWidgetData(data) })
  } catch (err) {
    const message = (err && err.message) || String(err)
    const denied = /forbidden|not granted|403|permission/i.test(message)
    write({ loading: false, settled: true, denied, error: message })
  }
}

/**
 * Normalize what an SDK entity returns into the ONE shape the block paths bind
 * against: `{ records, count, rows }`.
 *
 * A recipe author writes `itemsPath: 'records'` / `valuePath: 'count'` and gets
 * the same answer whether the entity returned a bare array or an enveloped
 * page. Any object shape is ALSO spread through untouched, so a richer entity's
 * own fields stay reachable by their real names.
 */
export const normalizeAiWidgetData = (data) => {
  if (Array.isArray(data)) return { records: data, rows: data, count: data.length }
  if (data && typeof data === 'object') {
    const rows = Array.isArray(data.records)
      ? data.records
      : Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.data)
          ? data.data
          : []
    return {
      ...data,
      records: rows,
      rows,
      count: typeof data.count === 'number' ? data.count : rows.length
    }
  }
  return { records: [], rows: [], count: 0, value: data }
}

/** The stashed fetch state for one widget id (never undefined). */
export const aiWidgetState = (root, id) =>
  (root && root._aiWidgetData && root._aiWidgetData[id]) || {}

// ─── Manage (owner-only) mutations ───────────────────────────────────────────
//
// "No UI to create/manage these outside chat… no 'share to workspace' button,
// no manage list, no delete affordance beyond the assistant" (tickets/opus.md
// "Per-workspace AI-CREATED widgets", additive remainder — the core shipped
// 2026-08-14, this closes the remainder). Reached from the SAME per-widget
// context menu every other widget already has (pages/main.js HomeModuleWidget
// onContextmenu → functions/homeContextMenu.js homeContextAction), gated on
// two booleans the SERVER stamps on every row: `viewerCanEdit` (share/
// unshare) and `viewerCanDelete` (delete) — see server
// WidgetDefService.viewerControlFor. The client never re-derives "am I the
// owner": `createdBy` is `supabaseUserId || auth.sub || _id`, and the shell
// has no client-side value that reliably compares against it. Showing the
// wrong action to the wrong viewer would be a client-only UX bug (the real
// gate is server-side and unchanged — PATCH/DELETE /core/widget-defs/:id
// re-check ownership on every call, every time), but a stamped boolean means
// it never happens at all.
//
// Both write the SERVER's fresh response back onto `root.workspaceAiWidgets`
// — never an optimistic client-side patch — because the response also
// re-stamps `viewerCanEdit`/`viewerCanDelete`/`viewerCapability` for the row's
// NEW state (e.g. sharing a personal widget can change who else can even see
// it). `_allModuleWidgetsFor` re-derives the third plane from this array on
// every root update, so a share/unshare/delete is visible to the acting
// member immediately with no extra fetch or page reload.

/**
 * Flip a widget's visibility between 'personal' and 'workspace'.
 *
 * @returns {Promise<{ ok: boolean, widget?: object, error?: string }>}
 */
export const setAiWidgetVisibility = async (root, sdk, widget, visibility) => {
  if (!root || !sdk || !widget || !widget.id) return { ok: false }
  const wsId = (root.workspace && (root.workspace.id || root.workspace._id)) || null
  try {
    const fresh = await sdk.execute('workspaceProject.widgetDefs', 'update', {
      id: widget.id,
      widget: { visibility },
      ...(wsId ? { workspaceId: wsId } : {})
    })
    const rows = Array.isArray(root.workspaceAiWidgets) ? root.workspaceAiWidgets : []
    root.update(
      { workspaceAiWidgets: rows.map((w) => (w && w.id === widget.id ? fresh : w)) },
      { preventFetch: true }
    )
    return { ok: true, widget: fresh }
  } catch (err) {
    return { ok: false, error: (err && err.message) || String(err) }
  }
}

/**
 * Delete a widget def. Removing it from `root.workspaceAiWidgets` is enough
 * to tear down its host: `_allModuleWidgetsFor` stops emitting the row on the
 * very next render, and `HomeModuleWidget.onRemove` (pages/main.js) already
 * frees the Gridstack cell for any widget host that unmounts — no special
 * case needed for the AI plane, same contract as hiding/uninstalling any
 * other widget.
 *
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const deleteAiWidgetDef = async (root, sdk, widget) => {
  if (!root || !sdk || !widget || !widget.id) return { ok: false }
  const wsId = (root.workspace && (root.workspace.id || root.workspace._id)) || null
  try {
    await sdk.execute('workspaceProject.widgetDefs', 'remove', {
      id: widget.id,
      ...(wsId ? { workspaceId: wsId } : {})
    })
    const rows = Array.isArray(root.workspaceAiWidgets) ? root.workspaceAiWidgets : []
    root.update(
      { workspaceAiWidgets: rows.filter((w) => !(w && w.id === widget.id)) },
      { preventFetch: true }
    )
    return { ok: true }
  } catch (err) {
    return { ok: false, error: (err && err.message) || String(err) }
  }
}
